// Styles
import 'vuetify/styles';
import '@/styles/main.scss';

import { createVuetify } from 'vuetify';
import type { ThemeDefinition } from 'vuetify'
import { h } from 'vue'

// Import SVG icons directly
import CloseIcon from '@/assets/icons/close.svg';
import MagnifyIcon from '@/assets/icons/magnify.svg';
import ChevronDownIcon from '@/assets/icons/chevron-down.svg';
import ChevronUpIcon from '@/assets/icons/chevron-up.svg';
import ChevronLeftIcon from '@/assets/icons/chevron-left.svg';
import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import CircleIcon from '@/assets/icons/circle.svg';
import PrinterIcon from '@/assets/icons/printer-outline.svg';
import ShareIcon from '@/assets/icons/share-variant-outline.svg';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg';

// Map of Vuetify size values to pixel values (matching MDI font sizes)
const sizeMap: Record<string, number> = {
  'x-small': 16,
  'small': 20,
  'default': 24,
  'medium': 28,
  'large': 32,
  'x-large': 36,
};

// Custom icon component for Vuetify
const CustomIcon = (props: Record<string, any>) => {
  const { icon, size, height, width, ...rest } = props;

  let finalSize: number;
  if (typeof size === 'string' && size in sizeMap) {
    finalSize = sizeMap[size];
  } else if (typeof size === 'string' && size.endsWith('px')) {
    finalSize = parseInt(size, 10);
  } else if (typeof size === 'number') {
    finalSize = size;
  } else {
    finalSize = sizeMap['default'];
  }

  const IconComponent = iconMap[icon];
  if (IconComponent) {
    return h('div', {
      style: {
        width: `${width || finalSize}px`,
        height: `${height || finalSize}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    }, [
      h(IconComponent, {
        ...rest,
        width: '100%',
        height: '100%',
        preserveAspectRatio: 'xMidYMid meet',
        style: {
          fill: 'currentColor',
          ...rest.style,
        },
      }),
    ]);
  }

  return null;
};

// Map of all used icons
const iconMap: Record<string, any> = {
  'mdi-close': CloseIcon,
  'mdi-magnify': MagnifyIcon,
  'mdi-chevron-down': ChevronDownIcon,
  'mdi-chevron-up': ChevronUpIcon,
  'mdi-chevron-left': ChevronLeftIcon,
  'mdi-chevron-right': ChevronRightIcon,
  'mdi-circle': CircleIcon,
  'mdi-printer-outline': PrinterIcon,
  'mdi-share-variant-outline': ShareIcon,
  'mdi-arrow-right': ArrowRightIcon,
};

const edReportsTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: '#007EAB',
    secondary: '#0A3E59',
    accent: '#D83F24',
    background: '#FFFFFF',
    surface: '#F2FAFC',
    error: '#D32F2F',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FF9800',
    textSecondary: '#0A3E59',
    
    'on-background': '#000000',
    'on-surface': '#000000',
    'on-primary': '#FFFFFF',
    'on-secondary': '#FFFFFF',
    'on-error': '#FFFFFF',
    'on-info': '#FFFFFF',
    'on-success': '#FFFFFF',
    'on-warning': '#000000',

    // Blue variations
    'darker-blue': '#041923',
    'dark-blue': '#0A3E59',
    'format-blue-2': '#557C90',
    'format-blue-3': '#90B9CE',
    'blue-border': '#0096CB33',
    'blue-border-transparent': 'rgba(0, 150, 204, 0.502)',
    'blue': '#0096cb',
    'blue-accessible-on-dark': '#00b0f0',
    'light-blue': '#ebf7fb',
    'light-blue-border': '#c6e3ed',
    'light-blue-pill': '#D2F4FF',
    'grey-blue': '#d9eff7',

    // Green variations
    'dark-green': '#637619',
    'green': '#9fbc28',
    'status-green': '#0B874D',
    'light-green': '#f0f5dd',
    'card-bg-green': '#ebf5f1',
    'card-border-green': '#b3d9c8',

    // Yellow variations
    'dark-yellow': '#916403',
    'yellow': '#f1a706',
    'light-yellow': '#fdf1d7',
    'status-yellow': '#CE8003',

    // Red variations
    'dark-red': '#b83a00',
    'red': '#f54e00',
    'light-red': '#fde3d6',
    'status-red': '#C72A4C',

    // Purple variations
    'purple': '#501041',
    'light-purple': '#af71a6',

    // Grayscale
    'black': '#000000',
    'tonal-black': '#02080c',
    'dark-grey': '#495965',
    'grey': '#666666',
    'light-grey': '#cccccc',
    'lighter-grey': '#d4d4d4',
    'lightest-grey': '#ededed',
    'meta-grey': '#6c8b9b',
    'white': '#ffffff',
    'shadow': 'rgba(0, 0, 0, 0.09098)',
    'grey-pill': '#DAE2E6',
    'grey-pill-transparent': 'rgba(73, 89, 101, 0.1)',
    'status-gray': '#d1d1d1',

    // Button
    'btn-gray': '#F2F2F2',

    // Border colors
    'border-light': 'rgba(0, 0, 0, 0.12)',
  },
};

// Create Vuetify instance
export default createVuetify({
  theme: {
    defaultTheme: 'edReportsTheme',
    themes: {
      edReportsTheme,
    }
  },
  icons: {
    defaultSet: 'custom',
    aliases: iconMap,
    sets: {
      custom: {
        component: CustomIcon,
      },
    },
  },
  defaults: {
    VCard: {
      color: 'white',
      elevation: 0,
      rounded: 3,
      class: 'custom-card-radius',
      style: 'border: 1px solid rgba(0, 0, 0, 0.12)',
    },
    VList: {
      rounded: 3,
    },
    VBtn: {
      variant: 'text',
      color: 'primary',
      class: 'text-none font-weight-regular',
      height: 40,
    },
    VIcon: {
      size: 'default',
    },
    VContainer: {
      maxWidth: {
        xxl: '1296px'
      }
    },
    VAlert: {
      icon: false,
      variant: 'tonal',
      class: 'text-uppercase',
    },
  },
});

// Export theme colors for direct use in components
export const themeColors = edReportsTheme.colors;
